import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Recycle, Plus, Search, Trash2, ListPlus, Car, Loader2, X, Edit } from 'lucide-react';
import { carsysApi } from '../api/carsysApi';
import { useData } from '../context/DataContext';
import { useT } from '../context/LanguageContext';
import { toast } from 'sonner';
import { makeDirtyClose } from '../lib/useDirtyClose';
import { confirmDialog } from '../lib/confirm';
import { VOERTUIG_TYPES } from '../components/AutoEditor';

const eur = (n) => (Number(n) || 0).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
const STATUSSEN = ['Onderdelenauto', 'Sloopauto', 'Afgerond'];

export default function Sloopautos({ onNavigate }) {
  const { t } = useT();
  const { rdwLookup } = useData();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [genItem, setGenItem] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems(await carsysApi.getSloopautos()); }
    catch (e) { toast.error(t('Laden mislukt', 'Laden mislukt'), { description: e.response?.data?.detail || e.message }); }
    finally { setLoading(false); }
  }, [t]);
  useEffect(() => { load(); }, [load]);

  const verwijder = async (s) => {
    if (!(await confirmDialog(`${t('Sloopauto verwijderen?', 'Sloopauto verwijderen?')}\n${s.merk} ${s.model} ${s.kenteken}`))) return;
    await carsysApi.deleteSloopauto(s.id); toast.success(t('Verwijderd', 'Verwijderd')); load();
  };

  return (
    <div className="space-y-4" data-testid="sloopautos-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Recycle size={22} /><div><h2 style={{ margin: 0 }}>{t('Sloopvoertuigen', 'Sloopvoertuigen')}</h2>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--cs-text-muted)' }}>{t('Markeer een voertuig (auto, scooter, motor, aanhanger…) en genereer automatisch een onderdelenlijst', 'Markeer een voertuig (auto, scooter, motor, aanhanger…) en genereer automatisch een onderdelenlijst')}</p></div>
        </div>
        <button className="cs-btn primary" onClick={() => setAdding(true)} data-testid="sloop-add"><Plus size={14} /> {t('Sloopvoertuig toevoegen', 'Sloopvoertuig toevoegen')}</button>
      </div>

      <div className="cs-card padded">
        {loading ? <div className="cs-empty">{t('Laden…', 'Laden…')}</div> : items.length === 0 ? (
          <div className="cs-empty" data-testid="sloop-empty">{t('Nog geen sloopauto\'s toegevoegd.', 'Nog geen sloopauto\'s toegevoegd.')}</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="cs-table" data-testid="sloop-table">
              <thead><tr><th>{t('Kenteken', 'Kenteken')}</th><th>{t('Voertuig', 'Voertuig')}</th><th>{t('Bouwjaar', 'Bouwjaar')}</th><th>{t('Km-stand', 'Km-stand')}</th><th>{t('Inkoopdatum', 'Inkoopdatum')}</th><th>{t('Demontage', 'Demontage')}</th><th>{t('Inkoop', 'Inkoop')}</th><th>{t('Verkocht', 'Verkocht')}</th><th>{t('Break-even', 'Break-even')}</th><th>{t('Status', 'Status')}</th><th>{t('Onderdelen', 'Onderdelen')}</th><th></th></tr></thead>
              <tbody>
                {items.map(s => (
                  <tr key={s.id} data-testid={`sloop-row-${s.id}`}>
                    <td data-label={t('Kenteken', 'Kenteken')} style={{ fontWeight: 700 }}>{s.kenteken}</td>
                    <td data-label={t('Voertuig', 'Voertuig')}>{[s.merk, s.model].filter(Boolean).join(' ') || '—'}{s.voertuigType && s.voertuigType !== 'Personenauto' ? <span style={{ marginLeft: 6, fontSize: 11, padding: '1px 7px', borderRadius: 10, background: 'var(--cs-accent-soft)', color: 'var(--cs-accent, #2563eb)' }}>{s.voertuigType}</span> : null}</td>
                    <td data-label={t('Bouwjaar', 'Bouwjaar')}>{s.bouwjaar || '—'}</td>
                    <td data-label={t('Km-stand', 'Km-stand')} data-testid={`sloop-km-${s.id}`}>{Number(s.kmStand) > 0 ? `${Number(s.kmStand).toLocaleString('nl-NL')} km` : '—'}</td>
                    <td data-label={t('Inkoopdatum', 'Inkoopdatum')} data-testid={`sloop-datum-${s.id}`}>{s.inkoopDatum ? new Date(s.inkoopDatum).toLocaleDateString('nl-NL') : '—'}</td>
                    <td data-label={t('Demontage', 'Demontage')} data-testid={`sloop-demontage-${s.id}`}>{s.demontageDatum ? new Date(s.demontageDatum).toLocaleDateString('nl-NL') : '—'}</td>
                    <td data-label={t('Inkoop', 'Inkoop')}>{eur(s.inkoopPrijs)}</td>
                    <td data-label={t('Verkocht', 'Verkocht')} data-testid={`sloop-verkocht-${s.id}`}>{eur(s.verkochtTotaal || 0)}{s.aantalVerkocht ? <span style={{ fontSize: 11, color: 'var(--cs-text-muted)' }}> · {s.aantalVerkocht}x</span> : null}</td>
                    <td data-label={t('Break-even', 'Break-even')} data-testid={`sloop-breakeven-${s.id}`}>
                      {(s.breakEvenRest || 0) > 0
                        ? <span style={{ color: '#d97706', fontWeight: 700 }} title={t('Nog nodig voor break-even', 'Nog nodig voor break-even')}>{t('nog', 'nog')} {eur(s.breakEvenRest)}</span>
                        : <span style={{ color: '#16a34a', fontWeight: 700 }} title={t('Winst', 'Winst')}>+{eur(s.winst || 0)}</span>}
                    </td>
                    <td data-label={t('Status', 'Status')}>{s.status}</td>
                    <td data-label={t('Onderdelen', 'Onderdelen')}><span style={{ fontWeight: 700 }}>{s.aantalOnderdelen || 0}</span></td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        <button className="cs-btn ghost" onClick={() => setGenItem(s)} data-testid={`sloop-gen-${s.id}`} title={t('Genereer onderdelenlijst', 'Genereer onderdelenlijst')}><ListPlus size={15} /> {t('Genereer', 'Genereer')}</button>
                        {(s.aantalOnderdelen > 0) && <button className="cs-btn ghost" onClick={() => onNavigate && onNavigate('oh-voorraad')} data-testid={`sloop-view-${s.id}`}>{t('Bekijk', 'Bekijk')}</button>}
                        <button className="cs-btn ghost" onClick={() => setEditing(s)} data-testid={`sloop-edit-${s.id}`} title={t('Bewerken', 'Bewerken')}><Edit size={15} /></button>
                        <button onClick={() => verwijder(s)} data-testid={`sloop-del-${s.id}`} style={{ width: 34, height: 32, border: '1px solid var(--cs-border)', borderRadius: 6, background: '#fff', color: '#dc2626', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><Trash2 size={15} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {adding && <SloopForm rdwLookup={rdwLookup} onClose={() => setAdding(false)} onSaved={load} />}
      {editing && <SloopForm rdwLookup={rdwLookup} existing={editing} onClose={() => setEditing(null)} onSaved={load} />}
      {genItem && <GenereerModal sloop={genItem} onClose={() => setGenItem(null)} onDone={load} />}
    </div>
  );
}

function SloopForm({ rdwLookup, existing, onClose, onSaved }) {
  const { t } = useT();
  const isEdit = !!existing?.id;
  const [d, setD] = useState(existing || { kenteken: '', voertuigType: 'Personenauto', merk: '', model: '', bouwjaar: '', kmStand: '', inkoopDatum: '', demontageDatum: '', inkoopPrijs: '', status: 'Onderdelenauto', notitie: '' });
  const [rdwLoading, setRdwLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setD(p => ({ ...p, [k]: v }));
  const initialSig = useRef(null);
  if (initialSig.current === null) initialSig.current = JSON.stringify(d);
  const requestClose = makeDirtyClose(() => JSON.stringify(d) !== initialSig.current, onClose, t);

  const handleRdw = async () => {
    if (!d.kenteken.trim()) { toast.error(t('Vul eerst het kenteken in', 'Vul eerst het kenteken in')); return; }
    setRdwLoading(true);
    try {
      const r = await rdwLookup(d.kenteken);
      if (r && r.merk) {
        const bj = r.datumEersteToelating ? parseInt(String(r.datumEersteToelating).slice(0, 4)) : d.bouwjaar;
        const vs = (r.voertuigsoort || '').toLowerCase();
        let vtype = d.voertuigType;
        if (vs.includes('bromfiets') || vs.includes('snorfiets')) vtype = 'Scooter';
        else if (vs.includes('motor')) vtype = 'Motor';
        else if (vs.includes('aanhang') || vs.includes('oplegger')) vtype = 'Aanhanger';
        else if (vs.includes('bedrijfs') || vs.includes('bestel')) vtype = 'Bestelbus';
        else if (vs.includes('vracht')) vtype = 'Vrachtwagen';
        else if (vs.includes('personen')) vtype = 'Personenauto';
        setD(p => ({ ...p, voertuigType: vtype, merk: r.merk || p.merk, model: r.handelsbenaming || p.model, bouwjaar: bj || p.bouwjaar }));
        toast.success(`RDW: ${r.merk} ${r.handelsbenaming || ''}`.trim());
      } else toast.error(t('RDW lookup mislukt', 'RDW lookup mislukt'));
    } catch (e) { toast.error(t('RDW lookup mislukt', 'RDW lookup mislukt'), { description: e.response?.data?.detail || e.message }); }
    finally { setRdwLoading(false); }
  };

  const save = async () => {
    if (!d.kenteken.trim() && !d.merk.trim()) { toast.error(t('Vul kenteken of merk in', 'Vul kenteken of merk in')); return; }
    setSaving(true);
    try {
      const payload = { ...d, kenteken: d.kenteken.trim().toUpperCase(), inkoopPrijs: Number(d.inkoopPrijs) || 0 };
      if (isEdit) { await carsysApi.updateSloopauto(existing.id, payload); toast.success(t('Sloopvoertuig bijgewerkt', 'Sloopvoertuig bijgewerkt')); }
      else { await carsysApi.createSloopauto(payload); toast.success(t('Sloopvoertuig toegevoegd', 'Sloopvoertuig toegevoegd')); }
      onSaved(); onClose();
    } catch (e) { toast.error(t('Opslaan mislukt', 'Opslaan mislukt'), { description: e.response?.data?.detail || e.message }); }
    finally { setSaving(false); }
  };

  return (
    <div className="cs-modal-overlay" onClick={requestClose}>
      <div className="cs-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }} data-testid="sloop-form">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}><Car size={18} /> {isEdit ? t('Sloopvoertuig bewerken', 'Sloopvoertuig bewerken') : t('Sloopvoertuig toevoegen', 'Sloopvoertuig toevoegen')}</h3>
          <button className="cs-btn ghost" onClick={requestClose}><X size={16} /></button>
        </div>
        <label style={lbl}>{t('Kenteken', 'Kenteken')}</label>
        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <input className="cs-input" value={d.kenteken} onChange={e => upd('kenteken', e.target.value)} onKeyDown={e => e.key === 'Enter' && handleRdw()} data-testid="sloop-kenteken" style={{ textTransform: 'uppercase', fontWeight: 600 }} />
          <button className="cs-btn" onClick={handleRdw} disabled={rdwLoading} data-testid="sloop-rdw">{rdwLoading ? <Loader2 size={14} className="spin" /> : <Search size={14} />} RDW</button>
        </div>
        <label style={lbl}>{t('Voertuigtype', 'Voertuigtype')}</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }} data-testid="sloop-voertuigtype">
          {VOERTUIG_TYPES.map(vt => (
            <button key={vt.v} type="button" onClick={() => upd('voertuigType', vt.v)} data-testid={`sloop-vtype-${vt.v}`}
              className={`cs-btn ${d.voertuigType === vt.v ? 'primary' : ''}`} style={{ padding: '5px 10px', fontSize: 12 }}>
              <span style={{ marginRight: 3 }}>{vt.icon}</span>{vt.label}
            </button>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <In label={t('Merk', 'Merk')} v={d.merk} on={v => upd('merk', v)} tid="sloop-merk" />
          <In label={t('Model', 'Model')} v={d.model} on={v => upd('model', v)} tid="sloop-model" />
          <In label={t('Bouwjaar', 'Bouwjaar')} v={d.bouwjaar} on={v => upd('bouwjaar', v)} tid="sloop-bouwjaar" />
          <In label={t('Kilometerstand', 'Kilometerstand')} v={d.kmStand} on={v => upd('kmStand', v)} tid="sloop-kmstand" type="number" />
          <In label={t('Inkoopdatum', 'Inkoopdatum')} v={d.inkoopDatum} on={v => upd('inkoopDatum', v)} tid="sloop-inkoopdatum" type="date" />
          <In label={t('Demontagedatum', 'Demontagedatum')} v={d.demontageDatum} on={v => upd('demontageDatum', v)} tid="sloop-demontagedatum" type="date" />
          <In label={t('Inkoopprijs', 'Inkoopprijs')} v={d.inkoopPrijs} on={v => upd('inkoopPrijs', v)} tid="sloop-inkoop" type="number" />
          <div><label style={lbl}>{t('Status', 'Status')}</label>
            <select className="cs-input" value={d.status} onChange={e => upd('status', e.target.value)} data-testid="sloop-status">{STATUSSEN.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
        </div>
        <label style={lbl}>{t('Notitie', 'Notitie')}</label>
        <textarea className="cs-input" rows={2} value={d.notitie} onChange={e => upd('notitie', e.target.value)} data-testid="sloop-notitie" style={{ resize: 'vertical' }} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
          <button className="cs-btn" onClick={requestClose}>{t('Annuleren', 'Annuleren')}</button>
          <button className="cs-btn primary" onClick={save} disabled={saving} data-testid="sloop-save">{t('Opslaan', 'Opslaan')}</button>
        </div>
        <style>{`.spin{animation:spin 1s linear infinite}@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>
      </div>
    </div>
  );
}

const lbl = { fontSize: 12, color: 'var(--cs-text-muted)', fontWeight: 600, display: 'block', margin: '8px 0 3px' };
const In = ({ label, v, on, tid, type = 'text' }) => (
  <div><label style={lbl}>{label}</label><input className="cs-input" type={type} value={v ?? ''} onChange={e => on(e.target.value)} data-testid={tid} /></div>
);

const STD_ONDERDELEN = [
  ['Koplamp links', 'Verlichting'], ['Koplamp rechts', 'Verlichting'],
  ['Achterlicht links', 'Verlichting'], ['Achterlicht rechts', 'Verlichting'],
  ['Buitenspiegel links', 'Carrosserie'], ['Buitenspiegel rechts', 'Carrosserie'],
  ['Motorblok', 'Motor'], ['Versnellingsbak', 'Transmissie'],
  ['Dynamo', 'Motor / elektrisch'], ['Startmotor', 'Motor / elektrisch'], ['Accu', 'Motor / elektrisch'],
  ['Radiateur', 'Koeling'],
  ['Portier linksvoor', 'Carrosserie'], ['Portier rechtsvoor', 'Carrosserie'],
  ['Portier linksachter', 'Carrosserie'], ['Portier rechtsachter', 'Carrosserie'],
  ['Motorkap', 'Carrosserie'], ['Achterklep', 'Carrosserie'],
  ['Voorbumper', 'Carrosserie'], ['Achterbumper', 'Carrosserie'],
  ['Remklauw linksvoor', 'Remsysteem'], ['Remklauw rechtsvoor', 'Remsysteem'],
  ['Schokdemper linksvoor', 'Ophanging'], ['Schokdemper rechtsvoor', 'Ophanging'],
  ['Stuurbekrachtigingspomp', 'Stuurinrichting'],
  ['Set velgen', 'Wielen / banden'],
  ['Stoel linksvoor', 'Interieur'], ['Stoel rechtsvoor', 'Interieur'], ['Achterbank', 'Interieur'],
];

function GenereerModal({ sloop, onClose, onDone }) {
  const { t } = useT();
  const [sel, setSel] = useState(() => STD_ONDERDELEN.map(() => true));
  const [custom, setCustom] = useState([]);
  const [naam, setNaam] = useState('');
  const [cat, setCat] = useState('Overig');
  const [busy, setBusy] = useState(false);

  const toggle = (i) => setSel(s => s.map((v, idx) => idx === i ? !v : v));
  const allChecked = sel.every(Boolean);
  const toggleAll = () => setSel(STD_ONDERDELEN.map(() => !allChecked));
  const addCustom = () => {
    if (!naam.trim()) return;
    setCustom(c => [...c, { naam: naam.trim(), categorie: cat || 'Overig' }]);
    setNaam('');
  };
  const gekozenAantal = sel.filter(Boolean).length + custom.length;

  const genereer = async () => {
    const onderdelen = [
      ...STD_ONDERDELEN.filter((_, i) => sel[i]).map(([n, c]) => ({ naam: n, categorie: c })),
      ...custom,
    ];
    if (!onderdelen.length) { toast.error(t('Selecteer minstens 1 onderdeel', 'Selecteer minstens 1 onderdeel')); return; }
    setBusy(true);
    try {
      const r = await carsysApi.genereerOnderdelen(sloop.id, { onderdelen });
      toast.success(t('Onderdelen gegenereerd', 'Onderdelen gegenereerd'), { description: `+${r.toegevoegd} ${t('onderdelen', 'onderdelen')}` });
      onDone(); onClose();
    } catch (e) { toast.error(t('Mislukt', 'Mislukt'), { description: e.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };

  return (
    <div className="cs-modal-overlay" onClick={onClose}>
      <div className="cs-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }} data-testid="gen-modal">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}><ListPlus size={18} /> {t('Onderdelen genereren', 'Onderdelen genereren')}</h3>
          <button className="cs-btn ghost" onClick={onClose}><X size={16} /></button>
        </div>
        <p style={{ margin: '0 0 10px', fontSize: 12.5, color: 'var(--cs-text-muted)' }}>{[sloop.merk, sloop.model, sloop.kenteken].filter(Boolean).join(' ')} · {t('Vink aan wat je wilt aanmaken', 'Vink aan wat je wilt aanmaken')}</p>
        <button className="cs-btn ghost" style={{ fontSize: 12, marginBottom: 6 }} onClick={toggleAll} data-testid="gen-toggle-all">{allChecked ? t('Alles uitvinken', 'Alles uitvinken') : t('Alles aanvinken', 'Alles aanvinken')}</button>
        <div style={{ maxHeight: 280, overflowY: 'auto', border: '1px solid var(--cs-border)', borderRadius: 8, padding: 8, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }} data-testid="gen-checklist">
          {STD_ONDERDELEN.map(([n, c], i) => (
            <label key={n} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, padding: '4px 6px', borderRadius: 6, cursor: 'pointer', background: sel[i] ? 'var(--cs-accent-soft)' : 'transparent' }} data-testid={`gen-item-${i}`}>
              <input type="checkbox" checked={sel[i]} onChange={() => toggle(i)} />
              <span>{n}</span>
            </label>
          ))}
          {custom.map((co, i) => (
            <label key={`c${i}`} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, padding: '4px 6px', borderRadius: 6, background: 'var(--cs-accent-soft)' }}>
              <input type="checkbox" checked readOnly />
              <span>{co.naam} <span style={{ color: 'var(--cs-text-muted)', fontSize: 11 }}>({co.categorie})</span></span>
              <button onClick={() => setCustom(c => c.filter((_, idx) => idx !== i))} style={{ marginLeft: 'auto', border: 'none', background: 'none', color: '#dc2626', cursor: 'pointer' }}><X size={13} /></button>
            </label>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
          <input className="cs-input" placeholder={t('Eigen onderdeel toevoegen…', 'Eigen onderdeel toevoegen…')} value={naam} onChange={e => setNaam(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }} data-testid="gen-custom-naam" />
          <select className="cs-input" style={{ maxWidth: 150 }} value={cat} onChange={e => setCat(e.target.value)} data-testid="gen-custom-cat">
            {['Motor', 'Motor / elektrisch', 'Transmissie', 'Carrosserie', 'Verlichting', 'Remsysteem', 'Ophanging', 'Stuurinrichting', 'Koeling', 'Wielen / banden', 'Interieur', 'Uitlaat', 'Overig'].map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <button className="cs-btn" onClick={addCustom} data-testid="gen-custom-add"><Plus size={14} /></button>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14 }}>
          <span style={{ fontSize: 12.5, color: 'var(--cs-text-muted)' }}>{gekozenAantal} {t('geselecteerd', 'geselecteerd')}</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="cs-btn" onClick={onClose}>{t('Annuleren', 'Annuleren')}</button>
            <button className="cs-btn primary" onClick={genereer} disabled={busy} data-testid="gen-confirm">{busy ? <Loader2 size={14} className="spin" /> : <ListPlus size={14} />} {t('Genereer onderdelen', 'Genereer onderdelen')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
